/*
 * ComponentDetailCard - 组件详情卡片
 * 可复用的组件详情展示卡片，用于 SystemDeploymentsSummary 和 SystemComponentsTable
 */

import React from 'react';
import {
  Box,
  Chip,
  Link,
  Typography,
  Tooltip,
  makeStyles,
  createStyles,
  Theme,
} from '@material-ui/core';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';
import HelpIcon from '@material-ui/icons/Help';
import SyncIcon from '@material-ui/icons/Sync';
import PauseCircleFilledIcon from '@material-ui/icons/PauseCircleFilled';
import { useRouteRef, useApi } from '@backstage/core-plugin-api';
import { entityRouteRef, useEntity } from '@backstage/plugin-catalog-react';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { environmentColors, getArgoCDProjectInfo, getKnativeResourceRefs } from './types';
import { useArgocdConfig } from '../argocd-enhanced/hooks/useArgocdConfig';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    componentRow: {
      padding: theme.spacing(2),
      borderRadius: theme.spacing(1),
      backgroundColor: theme.palette.background.default,
      marginBottom: theme.spacing(2),
      border: `1px solid ${theme.palette.divider}`,
      '&:last-child': {
        marginBottom: 0,
      },
      '&:hover': {
        backgroundColor: theme.palette.action.hover,
      },
    },
    componentHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: theme.spacing(1.5),
    },
    componentName: {
      fontWeight: 600,
      fontSize: '1rem',
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
    },
    componentType: {
      fontSize: '0.75rem',
      color: theme.palette.text.secondary,
      textTransform: 'uppercase',
      marginLeft: theme.spacing(1),
    },
    componentDescription: {
      fontSize: '0.75rem',
      color: theme.palette.text.secondary,
      whiteSpace: 'nowrap',
      fontStyle: 'italic',
      paddingLeft: theme.spacing(1),
      borderLeft: `2px solid ${theme.palette.divider}`,
    },
    statusChips: {
      display: 'flex',
      gap: theme.spacing(1),
      flexWrap: 'wrap',
    },
    statusChip: {
      height: 24,
      fontSize: '0.75rem',
    },
    infoGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(6, 1fr)',
      gap: theme.spacing(1.5),
      marginTop: theme.spacing(1.5),
      [theme.breakpoints.down('md')]: {
        gridTemplateColumns: 'repeat(3, 1fr)',
      },
      [theme.breakpoints.down('sm')]: {
        gridTemplateColumns: '1fr',
      },
    },
    infoItem: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(0.5),
    },
    infoLabel: {
      fontSize: '0.75rem',
      color: theme.palette.text.secondary,
      fontWeight: 500,
    },
    infoValue: {
      fontSize: '0.875rem',
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(0.5),
      fontFamily: 'monospace',
    },
    envBadge: {
      fontSize: '0.75rem',
      fontWeight: 600,
      padding: '2px 8px',
      borderRadius: theme.spacing(0.5),
    },
    statusIcon: {
      fontSize: '1rem',
    },
    healthyIcon: {
      color: '#4caf50',
    },
    degradedIcon: {
      color: '#f44336',
    },
    progressingIcon: {
      color: '#2196f3',
    },
    suspendedIcon: {
      color: '#ff9800',
    },
    unknownIcon: {
      color: '#9e9e9e',
    },
    noDataText: {
      color: theme.palette.text.secondary,
      fontStyle: 'italic',
    },
    ownerLink: {
      color: theme.palette.primary.main,
      textDecoration: 'none',
      '&:hover': {
        textDecoration: 'underline',
      },
    },
  }),
);

// ArgoCD 健康状态类型
type HealthStatus = 'Healthy' | 'Progressing' | 'Degraded' | 'Suspended' | 'Missing' | 'Unknown';

// 组件详情数据
export interface ComponentDetailData {
  componentName: string;
  componentRef: string;
  componentType?: string;
  componentDescription?: string;
  owner?: string;
  ownerRef?: string;
  lifecycle?: string;
  appName?: string;
  instanceName?: string;
  healthStatus: HealthStatus;
  syncStatus: string;
  hasArgoCD: boolean;
  clusterName?: string;
  server?: string;
  namespace?: string;
  environment?: string;
  environmentLabel?: string;
  syncRevision?: string;
  lastSyncTime?: string;
  // Knative 相关字段
  poweredBy?: 'knative' | 'argocd' | string;
  knativeServiceName?: string;
  knativeNamespace?: string;
}

const StatusIcon: React.FC<{ status: HealthStatus }> = ({ status }) => {
  const classes = useStyles();
  
  switch (status) {
    case 'Healthy':
      return <CheckCircleIcon className={`${classes.statusIcon} ${classes.healthyIcon}`} />;
    case 'Degraded':
    case 'Missing':
      return <ErrorIcon className={`${classes.statusIcon} ${classes.degradedIcon}`} />;
    case 'Progressing':
      return <SyncIcon className={`${classes.statusIcon} ${classes.progressingIcon}`} />;
    case 'Suspended':
      return <PauseCircleFilledIcon className={`${classes.statusIcon} ${classes.suspendedIcon}`} />;
    default:
      return <HelpIcon className={`${classes.statusIcon} ${classes.unknownIcon}`} />;
  }
};

const getHealthStatusColor = (status: HealthStatus): string => {
  switch (status) {
    case 'Healthy':
      return '#4caf50';
    case 'Degraded':
    case 'Missing':
      return '#f44336';
    case 'Progressing':
      return '#2196f3';
    case 'Suspended':
      return '#ff9800';
    default:
      return '#9e9e9e';
  }
};

const getSyncStatusColor = (status: string): string => {
  switch (status) {
    case 'Synced':
      return '#4caf50';
    case 'OutOfSync':
      return '#ff9800';
    default:
      return '#9e9e9e';
  }
};

interface ComponentDetailCardProps {
  detail: ComponentDetailData;
  showOwner?: boolean;
}

export const ComponentDetailCard: React.FC<ComponentDetailCardProps> = ({ detail, showOwner = false }) => {
  const classes = useStyles();
  const entityRoute = useRouteRef(entityRouteRef);
  const { entity } = useEntity(); // 获取 System entity
  const { baseUrl, instances } = useArgocdConfig();
  const catalogApi = useApi(catalogApiRef);
  
  // 从 System entity 获取 ArgoCD Project 信息
  const argocdProject = getArgoCDProjectInfo(entity);
  
  // 使用 useMemo 缓存 resourceRef，避免无限循环
  const argocdProjectResourceRef = React.useMemo(() => argocdProject?.resourceRef, [argocdProject?.resourceRef]);
  
  // 获取 ArgoCD Project Resource entity 的详细信息
  const [argocdProjectResource, setArgoCDProjectResource] = React.useState<any>(null);
  
  React.useEffect(() => {
    let cancelled = false;
    if (argocdProjectResourceRef) {
      catalogApi.getEntityByRef(argocdProjectResourceRef)
        .then(resource => {
          if (!cancelled && resource) {
            setArgoCDProjectResource(resource);
          }
        })
        .catch(() => {
          // 获取失败，忽略
        });
    }
    return () => {
      cancelled = true;
    };
  }, [argocdProjectResourceRef, catalogApi]);

  // 从 System entity 获取 Knative Resource 信息
  const knativeResources = React.useMemo(() => getKnativeResourceRefs(entity), [entity]);
  
  // 根据当前组件的环境匹配对应的 Knative Resource
  const matchedKnativeResourceRef = React.useMemo(() => {
    if (detail.poweredBy !== 'knative' || !detail.environment) {
      return null;
    }
    const matched = knativeResources.find(r => r.environment === detail.environment);
    return matched?.resourceRef || null;
  }, [detail.poweredBy, detail.environment, knativeResources]);
  
  // 获取 Knative Project Resource entity 的详细信息
  const [knativeProjectResource, setKnativeProjectResource] = React.useState<any>(null);
  
  React.useEffect(() => {
    let cancelled = false;
    if (matchedKnativeResourceRef) {
      catalogApi.getEntityByRef(matchedKnativeResourceRef)
        .then(resource => {
          if (!cancelled && resource) {
            setKnativeProjectResource(resource);
          }
        })
        .catch(() => {
          // 获取失败，忽略
        });
    }
    return () => {
      cancelled = true;
    };
  }, [matchedKnativeResourceRef, catalogApi]);

  // 解析 componentRef: component:default/component-name
  const refParts = detail.componentRef.replace('component:', '').split('/');
  const componentNamespace = refParts[0] || 'default';
  const componentPath = entityRoute({
    kind: 'component',
    namespace: componentNamespace,
    name: detail.componentName,
  });

  // 解析 owner 路径
  let ownerPath: string | undefined;
  if (showOwner && detail.ownerRef) {
    const ownerParts = detail.ownerRef.split('/');
    const ownerName = ownerParts[ownerParts.length - 1];
    const ownerKind = detail.ownerRef.split(':')[0] || 'group';
    const ownerNamespace = ownerParts.length > 1 
      ? ownerParts[0].split(':')[1] || 'default'
      : 'default';
    ownerPath = entityRoute({
      kind: ownerKind,
      namespace: ownerNamespace,
      name: ownerName,
    });
  }

  // 构建 ArgoCD 应用链接
  const buildArgoCDAppUrl = (): string | undefined => {
    if (!detail.appName) return undefined;
    
    // 获取 ArgoCD 实例的 URL
    // 优先使用 ArgoCD Project 中的实例信息，其次使用 detail 中的 instanceName
    const instanceName = argocdProject?.instanceName || detail.instanceName;
    let argoCDBaseUrl = baseUrl;
    
    if (instanceName && instances.length > 0) {
      const instance = instances.find(inst => inst.name === instanceName);
      if (instance?.url) {
        argoCDBaseUrl = instance.url;
      }
    }
    
    if (!argoCDBaseUrl) return undefined;
    
    // ArgoCD 应用链接格式：
    // - 如果应用在 ArgoCD Project 命名空间中：${baseUrl}/applications/${projectNamespace}/${appName}
    // - 否则：${baseUrl}/applications/${appName}
    // 通常 ArgoCD 应用在 argocd namespace 或与 Project 同名的 namespace
    const projectNamespace = argocdProject?.projectName || 'argocd';
    return `${argoCDBaseUrl.replace(/\/$/, '')}/applications/${projectNamespace}/${detail.appName}`;
  };

  const argoCDAppUrl = buildArgoCDAppUrl();

  return (
    <Box className={classes.componentRow}>
      {/* 组件头部：名称、描述和状态在同一行 */}
      <Box className={classes.componentHeader}>
        <Box style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 12 }}>
          <Typography className={classes.componentName}>
            <Link href={componentPath} className={classes.ownerLink} style={{ fontWeight: 600 }}>
              {detail.componentName}
            </Link>
            {detail.componentType && (
              <Chip
                label={detail.componentType}
                size="small"
                className={classes.componentType}
                style={{
                  backgroundColor: '#e0e0e0',
                  color: '#424242',
                  height: 20,
                  fontSize: '0.7rem',
                }}
              />
            )}
          </Typography>
          {detail.componentDescription && (
            <Typography className={classes.componentDescription} style={{ margin: 0 }}>
              {detail.componentDescription.replace(/Knative Service/g, '服务')}
            </Typography>
          )}
        </Box>
        <Box className={classes.statusChips}>
          {detail.poweredBy === 'knative' ? (
            <Chip
              size="small"
              icon={<StatusIcon status={detail.healthStatus} />}
              label={detail.healthStatus}
              className={classes.statusChip}
              style={{
                backgroundColor: `${getHealthStatusColor(detail.healthStatus)}20`,
                color: getHealthStatusColor(detail.healthStatus),
              }}
            />
          ) : detail.hasArgoCD ? (
            <>
              <Chip
                size="small"
                icon={<StatusIcon status={detail.healthStatus} />}
                label={detail.healthStatus}
                className={classes.statusChip}
                style={{
                  backgroundColor: `${getHealthStatusColor(detail.healthStatus)}20`,
                  color: getHealthStatusColor(detail.healthStatus),
                }}
              />
              <Chip
                size="small"
                label={detail.syncStatus}
                className={classes.statusChip}
                style={{
                  backgroundColor: `${getSyncStatusColor(detail.syncStatus)}20`,
                  color: getSyncStatusColor(detail.syncStatus),
                }}
              />
            </>
          ) : (
            <Typography variant="caption" className={classes.noDataText}>
              未配置 ArgoCD
            </Typography>
          )}
        </Box>
      </Box>
      {/* 详细信息网格 */}
      {detail.hasArgoCD && (
        <Box className={classes.infoGrid}>
          {/* 环境 */}
          {detail.environmentLabel && (
            <Box className={classes.infoItem}>
              <Typography className={classes.infoLabel}>环境</Typography>
              <Chip
                label={detail.environmentLabel}
                size="small"
                className={classes.envBadge}
                style={{
                  backgroundColor: environmentColors[detail.environment || 'test']?.bg || '#e3f2fd',
                  color: environmentColors[detail.environment || 'test']?.text || '#1565c0',
                  width: 'fit-content',
                }}
              />
            </Box>
          )}

          {/* ArgoCD 应用名称 */}
          {detail.appName && (
            <Box className={classes.infoItem}>
              <Typography className={classes.infoLabel}>ArgoCD 应用</Typography>
              {argoCDAppUrl ? (
                <Link 
                  href={argoCDAppUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className={classes.ownerLink}
                  style={{ fontSize: '0.875rem' }}
                >
                  {detail.appName}
                </Link>
              ) : (
                <Typography className={classes.infoValue}>{detail.appName}</Typography>
              )}
            </Box>
          )}

          {/* ArgoCD 项目 */}
          {argocdProject && (
            <Box className={classes.infoItem}>
              <Typography className={classes.infoLabel}>ArgoCD 项目</Typography>
              <Tooltip
                title={
                  <Box style={{ maxWidth: 400 }}>
                    {argocdProjectResource ? (
                      <>
                        <Typography variant="body2" style={{ fontWeight: 600, marginBottom: 8 }}>
                          {argocdProjectResource.metadata.title || argocdProject.projectName}
                        </Typography>
                        
                        {/* ArgoCD 地址 */}
                        {argocdProjectResource.metadata.annotations?.['argocd.argoproj.io/instance-url'] && (
                          <Box style={{ marginBottom: 8 }}>
                            <Typography variant="caption" style={{ fontWeight: 600, display: 'block', marginBottom: 2 }}>
                              ArgoCD 地址:
                            </Typography>
                            <Typography variant="caption" style={{ display: 'block', wordBreak: 'break-all' }}>
                              {argocdProjectResource.metadata.annotations['argocd.argoproj.io/instance-url']}
                            </Typography>
                          </Box>
                        )}
                        
                        {/* Destinations 信息 - 只显示匹配当前 component 环境的那一条 */}
                        {(() => {
                          const destinationsStr = argocdProjectResource.metadata.annotations?.['hose.io/destinations'];
                          if (!destinationsStr) return null;
                          
                          try {
                            const destinations = JSON.parse(destinationsStr);
                            if (!Array.isArray(destinations) || destinations.length === 0) return null;
                            
                            // 简化匹配：根据 clusterName（来自 argocd/dest-server）匹配 destination
                            const matchedDestination = detail.clusterName
                              ? destinations.find((dest: any) => 
                                  dest.name === detail.clusterName || 
                                  dest.clusterName === detail.clusterName
                                )
                              : null;
                            
                            // 如果没找到，使用第一个作为 fallback
                            const finalDestination = matchedDestination || destinations[0];
                            
                            if (finalDestination) {
                              return (
                                <Box>
                                  <Typography variant="caption" style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>
                                    Destination ({detail.environmentLabel || detail.environment || '当前环境'}):
                                  </Typography>
                                  <Box style={{ padding: 4, backgroundColor: 'rgba(255,255,255,0.1)', borderRadius: 4 }}>
                                    <Typography variant="caption" style={{ display: 'block' }}>
                                      <strong>Server:</strong> {finalDestination.server || '-'}
                                    </Typography>
                                    <Typography variant="caption" style={{ display: 'block' }}>
                                      <strong>Namespace:</strong> {finalDestination.namespace || '-'}
                                    </Typography>
                                    <Typography variant="caption" style={{ display: 'block' }}>
                                      <strong>Name:</strong> {finalDestination.name || '-'}
                                    </Typography>
                                    <Typography variant="caption" style={{ display: 'block' }}>
                                      <strong>ClusterName:</strong> {finalDestination.clusterName || finalDestination.name || '-'}
                                    </Typography>
                                  </Box>
                                </Box>
                              );
                            }
                            return null;
                          } catch (e) {
                            return null;
                          }
                        })()}
                      </>
                    ) : (
                      <Typography variant="body2">
                        {argocdProject.projectName} ({argocdProject.instanceName})
                      </Typography>
                    )}
                  </Box>
                }
                arrow
                placement="top"
                interactive
              >
                <Typography className={classes.infoValue} style={{ cursor: 'help' }}>
                  {argocdProject.projectName}
                </Typography>
              </Tooltip>
            </Box>
          )}

          {/* 集群名称 */}
          {detail.clusterName && (
            <Box className={classes.infoItem}>
              <Typography className={classes.infoLabel}>集群</Typography>
              <Typography className={classes.infoValue}>{detail.clusterName}</Typography>
            </Box>
          )}

          {/* 命名空间 */}
          {detail.namespace && (
            <Box className={classes.infoItem}>
              <Typography className={classes.infoLabel}>命名空间</Typography>
              <Typography className={classes.infoValue}>{detail.namespace}</Typography>
            </Box>
          )}

          {/* Server */}
          {detail.server && (
            <Box className={classes.infoItem}>
              <Typography className={classes.infoLabel}>Server</Typography>
              <Tooltip title={detail.server} arrow>
                <Typography className={classes.infoValue} style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {detail.server}
                </Typography>
              </Tooltip>
            </Box>
          )}

          {/* 同步版本 */}
          {detail.syncRevision && (
            <Box className={classes.infoItem}>
              <Typography className={classes.infoLabel}>同步版本</Typography>
              <Typography className={classes.infoValue} style={{ fontSize: '0.8rem' }}>
                {detail.syncRevision.substring(0, 7)}
              </Typography>
            </Box>
          )}

          {/* 最后同步时间 - 和 Owner 信息在同一行 */}
          {detail.lastSyncTime && (
            <Box style={{ 
              gridColumn: '1 / -1', 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              marginTop: 0,
            }}>
              <Box style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Typography className={classes.infoLabel} style={{ margin: 0 }}>最后同步</Typography>
                <Typography className={classes.infoValue} style={{ fontSize: '0.8rem', margin: 0 }}>
                  {new Date(detail.lastSyncTime).toLocaleString('zh-CN')}
                </Typography>
              </Box>
              {showOwner && detail.owner && (
                <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                  <Typography variant="caption" color="textSecondary">
                    Owner:{' '}
                    {ownerPath ? (
                      <Link href={ownerPath} className={classes.ownerLink}>
                        {detail.owner}
                      </Link>
                    ) : (
                      detail.owner
                    )}
                  </Typography>
                  {/* {detail.lifecycle && (
                    <Chip
                      label={detail.lifecycle}
                      size="small"
                      style={{
                        height: 20,
                        fontSize: '0.7rem',
                      }}
                    />
                  )} */}
                </Box>
              )}
            </Box>
          )}
        </Box>
      )}

      {/* Knative 组件详细信息 */}
      {detail.poweredBy === 'knative' && (
        <Box className={classes.infoGrid}>
          {/* 环境 */}
          {detail.environmentLabel && (
            <Box className={classes.infoItem}>
              <Typography className={classes.infoLabel}>环境</Typography>
              <Chip
                label={detail.environmentLabel}
                size="small"
                className={classes.envBadge}
                style={{
                  backgroundColor: environmentColors[detail.environment || 'test']?.bg || '#e3f2fd',
                  color: environmentColors[detail.environment || 'test']?.text || '#1565c0',
                  width: 'fit-content',
                }}
              />
            </Box>
          )}

          {/* Knative Service 名称 */}
          {detail.knativeServiceName && (
            <Box className={classes.infoItem}>
              <Typography className={classes.infoLabel}>Knative Service</Typography>
              <Typography className={classes.infoValue}>{detail.knativeServiceName}</Typography>
            </Box>
          )}

          {/* 集群名称 - 带 Tooltip 显示 Knative Project Resource 信息 */}
          {detail.clusterName && (
            <Box className={classes.infoItem}>
              <Typography className={classes.infoLabel}>集群</Typography>
              <Tooltip
                title={
                  <Box style={{ maxWidth: 400 }}>
                    {knativeProjectResource ? (
                      <>
                        <Typography variant="body2" style={{ fontWeight: 600, marginBottom: 8 }}>
                          {knativeProjectResource.metadata.title || knativeProjectResource.metadata.name}
                        </Typography>
                        
                        {/* Knative 集群信息 */}
                        {knativeProjectResource.metadata.annotations?.['hose.io/knative-cluster'] && (
                          <Box style={{ marginBottom: 8 }}>
                            <Typography variant="caption" style={{ fontWeight: 600, display: 'block', marginBottom: 2 }}>
                              集群:
                            </Typography>
                            <Typography variant="caption" style={{ display: 'block' }}>
                              {knativeProjectResource.metadata.annotations['hose.io/knative-cluster']}
                            </Typography>
                          </Box>
                        )}
                        
                        {/* Knative 集群地址 */}
                        {knativeProjectResource.metadata.annotations?.['hose.io/knative-cluster-url'] && (
                          <Box style={{ marginBottom: 8 }}>
                            <Typography variant="caption" style={{ fontWeight: 600, display: 'block', marginBottom: 2 }}>
                              集群地址:
                            </Typography>
                            <Typography variant="caption" style={{ display: 'block', wordBreak: 'break-all' }}>
                              {knativeProjectResource.metadata.annotations['hose.io/knative-cluster-url']}
                            </Typography>
                          </Box>
                        )}
                        
                        {/* 命名空间 */}
                        {detail.knativeNamespace && (
                          <Box style={{ marginBottom: 8 }}>
                            <Typography variant="caption" style={{ fontWeight: 600, display: 'block', marginBottom: 2 }}>
                              命名空间:
                            </Typography>
                            <Typography variant="caption" style={{ display: 'block' }}>
                              {detail.knativeNamespace}
                            </Typography>
                          </Box>
                        )}
                        
                        {/* Service 名称 */}
                        {detail.knativeServiceName && (
                          <Box>
                            <Typography variant="caption" style={{ fontWeight: 600, display: 'block', marginBottom: 2 }}>
                              Service:
                            </Typography>
                            <Typography variant="caption" style={{ display: 'block' }}>
                              {detail.knativeServiceName}
                            </Typography>
                          </Box>
                        )}
                      </>
                    ) : (
                      <Typography variant="body2">
                        {detail.clusterName}
                      </Typography>
                    )}
                  </Box>
                }
                arrow
                placement="top"
                interactive
              >
                <Typography className={classes.infoValue} style={{ cursor: 'help' }}>
                  {detail.clusterName}
                </Typography>
              </Tooltip>
            </Box>
          )}

          {/* 命名空间 */}
          {detail.knativeNamespace && (
            <Box className={classes.infoItem}>
              <Typography className={classes.infoLabel}>命名空间</Typography>
              <Typography className={classes.infoValue}>{detail.knativeNamespace}</Typography>
            </Box>
          )}

          {/* 当前 Revision（复用 syncRevision 字段） */}
          {detail.syncRevision && (
            <Box className={classes.infoItem}>
              <Typography className={classes.infoLabel}>当前 Revision</Typography>
              <Typography className={classes.infoValue} style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                {detail.syncRevision}
              </Typography>
            </Box>
          )}

          {/* 流量分配（复用 lastSyncTime 字段）- 带 Tooltip 显示详细流量信息 */}
          {detail.lastSyncTime && (
            <Box className={classes.infoItem}>
              <Typography className={classes.infoLabel}>流量分配</Typography>
              <Tooltip
                title={
                  <Box style={{ maxWidth: 350 }}>
                    <Typography variant="body2" style={{ fontWeight: 600, marginBottom: 8 }}>
                      流量分配详情:
                    </Typography>
                    <Box style={{ 
                      padding: 8, 
                      backgroundColor: 'rgba(255,255,255,0.1)', 
                      borderRadius: 4,
                      marginBottom: 8,
                    }}>
                      <Typography variant="caption" style={{ 
                        display: 'block', 
                        fontFamily: 'monospace',
                        whiteSpace: 'pre-wrap',
                        lineHeight: 1.6,
                      }}>
                        {detail.lastSyncTime.split(', ').map((item, idx) => (
                          <React.Fragment key={idx}>
                            {item}
                            {idx < detail.lastSyncTime.split(', ').length - 1 && '\n'}
                          </React.Fragment>
                        ))}
                      </Typography>
                    </Box>
                    {detail.syncRevision && (
                      <Typography variant="caption" style={{ display: 'block', marginBottom: 4 }}>
                        <strong>当前 Revision:</strong> {detail.syncRevision}
                      </Typography>
                    )}
                    {detail.knativeServiceName && detail.knativeNamespace && (
                      <Typography variant="caption" style={{ display: 'block', color: 'rgba(255,255,255,0.7)' }}>
                        <strong>Service:</strong> {detail.knativeServiceName}
                        <br />
                        <strong>Namespace:</strong> {detail.knativeNamespace}
                      </Typography>
                    )}
                  </Box>
                }
                arrow
                placement="top"
                interactive
              >
                <Typography className={classes.infoValue} style={{ fontSize: '0.8rem', cursor: 'help' }}>
                  {detail.lastSyncTime}
                </Typography>
              </Tooltip>
            </Box>
          )}

          {/* Owner 信息 */}
          {showOwner && detail.owner && (
            <Box style={{ 
              gridColumn: '1 / -1', 
              display: 'flex', 
              justifyContent: 'flex-end', 
              alignItems: 'center',
              marginTop: 0,
            }}>
              <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                <Typography variant="caption" color="textSecondary">
                  Owner:{' '}
                  {ownerPath ? (
                    <Link href={ownerPath} className={classes.ownerLink}>
                      {detail.owner}
                    </Link>
                  ) : (
                    detail.owner
                  )}
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

